import React, { useState, useEffect } from 'react';
import { Container, Col, Row, Form, Button } from 'react-bootstrap';
import { Link, useParams, useNavigate, useLocation } from 'react-router-dom';
import { showToast } from '../config/Alert';
import call from "../assets/images/call.png";
import arrowLeft from "../assets/images/arrow-left.png";
import { useCart } from "../context/CartContext";
import { getChildren } from '../services/ProfileApi';
import { getCourseDetails, getTimeSlots } from '../services/ProgramApi';
import LoadingSpinner from './LoadingSpinner';
import { getCartTypeFromItems } from '../context/CartContext';

const PickList = () => {
  const user = JSON.parse(localStorage.getItem("user"));
  const { id } = useParams();
  const navigate = useNavigate();
  const [subPrograms, setSubPrograms] = useState([]);
  const [skillLevels, setSkillLevels] = useState([]);
  const [paymentPlans, setPaymentPlans] = useState([]);
  const [children, setChildren] = useState([]);
  const [timeSlots, setTimeSlots] = useState([]);
  const [selectedChild, setSelectedChild] = useState("");
  const [selectedFocus, setSelectedFocus] = useState("");
  const [selectedSkillLevel, setSelectedSkillLevel] = useState("");
  const [filteredSkillLevels, setFilteredSkillLevels] = useState([]);
  const [selectedPaymentPlan, setSelectedPaymentPlan] = useState("");
  const [selectedDay1, setSelectedDay1] = useState("");
  const [selectedDay2, setSelectedDay2] = useState("");
  const [selectedTime1, setSelectedTime1] = useState("");
  const [selectedTime2, setSelectedTime2] = useState("");
  const [selectedChildStatus, setSelectedChildStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const { addToCart, fetchCart } = useCart();
  const location = useLocation();
  const renewalData = location.state?.renewalData || null;
  const isRenewal = !!renewalData;

  useEffect(() => {
    fetchData();
  }, [id]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const childrenData = await getChildren(user.id);
      setChildren(childrenData.data);

      const programData = await getCourseDetails(id);
      setSubPrograms(programData.data.sub_programs);
      //  Set default when subprograms are not present
      if (programData.data.sub_programs?.length === 0) {
        setFilteredSkillLevels(programData.data.skill_levels || []);
      }
      setSkillLevels(programData.data.skill_levels);
      setPaymentPlans(programData.data.payment_plans);

      if (isRenewal) {
        setSelectedChild(renewalData.child_id);
        setSelectedFocus(renewalData?.sub_program_focus?.title || "");
        setSelectedSkillLevel(renewalData.skill_level_id);
        setSelectedChildStatus(1);
        fetchTimeSlots(renewalData.skill_level_id);
      }
      setLoading(false);
    } catch (error) {
      showToast("Error fetching data", 'error');
    }
  };

  // Get Time Slots Data
  const fetchTimeSlots = async (levelId) => {
    try {
      const response = await getTimeSlots(levelId);
      setTimeSlots(response.data.time_slots);
    } catch (error) {
      showToast("Error fetching time slots", 'error');
    }
  };

  const uniqueDays = [...new Set(timeSlots.flatMap(slot => slot.week_days))];

  const getTimeRangesForDay = (selectedDay) => {
    return timeSlots
      .filter(slot => slot.week_days.includes(parseInt(selectedDay)))
      .flatMap(slot => slot.time_ranges);
  };

  // Filter time slots based on selected day
  const filteredTimeSlots1 = timeSlots.filter(slot => slot.week_days.includes(parseInt(selectedDay1)));
  const filteredTimeSlots2 = timeSlots.filter(slot => slot.week_days.includes(parseInt(selectedDay2)));

  const handleAddToCart = async () => {
    const cartData = await fetchCart();
    const currentCartItems = cartData?.data || [];
    const existingCartType = getCartTypeFromItems(currentCartItems); // using helper here
    const currentType = isRenewal ? 'renewal' : 'new';

    if (existingCartType && existingCartType !== currentType) {
      showToast(`You already have a ${existingCartType} item in your cart. Please checkout or clear your cart to add a ${currentType} item.`, 'error');
      return;
    }

    if (!selectedChild) {
      showToast("Please select a child first", 'error');
      return;
    }

    if ((subPrograms.length > 0 && !selectedFocus) || !selectedSkillLevel || !selectedPaymentPlan || !selectedDay1 || !selectedTime1 || !selectedDay2 || !selectedTime2) {
      showToast("All fields must be selected", 'error');
      return;
    }

    if (selectedDay1 === selectedDay2 && selectedTime1 === selectedTime2) {
      showToast("Slot 1 and Slot 2 cannot have the same day and time. Please select different slots.", 'error');
      return;
    }

    const timeSlot1 = filteredTimeSlots1.find(slot => slot.time_ranges.some(range => `${range.start_time}-${range.end_time}` === selectedTime1));
    const timeSlot2 = filteredTimeSlots2.find(slot => slot.time_ranges.some(range => `${range.start_time}-${range.end_time}` === selectedTime2));

    const timeSlotId1 = timeSlot1 ? timeSlot1.id : null;
    const timeSlotId2 = timeSlot2 ? timeSlot2.id : null;

    const payload = {
      user_id: user.id,
      child_id: selectedChild,
      program_id: id,
      sub_program_id: subPrograms.length > 0 && subPrograms[0].id || null,
      // focus_id: subPrograms.find(sub => sub.images.some(image => image.title === selectedFocus))?.id || selectedFocus,
      focus_id: selectedFocus,
      skill_level_id: selectedSkillLevel,
      payment_plan_id: selectedPaymentPlan,
      booking_type: isRenewal ? "renewal" : "new",
      time_slots: [
        {
          day: selectedDay1,
          time_slot_id: timeSlotId1,
          start_time: selectedTime1.split('-')[0],
          end_time: selectedTime1.split('-')[1]
        },
        {
          day: selectedDay2,
          time_slot_id: timeSlotId2,
          start_time: selectedTime2.split('-')[0],
          end_time: selectedTime2.split('-')[1]
        }
      ]
    };

    setLoading(true);
    const response = await addToCart(payload);
    if (response.ok) {
      showToast("Program added to cart successfully", 'success');
      navigate(`/cart`, { state: { subscriptionId: renewalData?.subscription_id } });
    }
    setLoading(false);
  };

  // const filteredSkillLevels = selectedFocus
  //   ? skillLevels.filter(skill => skill.time_slots?.some(slot => slot.focus_id === parseInt(selectedFocus)))
  //   : subPrograms.length === 0 ? skillLevels : [];

  const handleFocusChange = (value) => {
    setSelectedFocus(value);
    setSelectedSkillLevel("");

    const filtered = skillLevels.filter(skill => skill.focus_id === parseInt(value));
    setFilteredSkillLevels(filtered);
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <div className='app-container mobile-bottom'>
      <Container className='pickaList_select'>
        <div className='breadcrum bg_light_yellow'>
          <h2 className='d-flex align-items-center mb-0 fs-20'>
            <Link to={`/course/${id}`}><img src={arrowLeft} width={18} className='me-2' alt="link" /></Link>
            Pick A Slot</h2>
        </div>

        <Row className='pt-4'>
          <Col xs='8' md='4'>
            <Form.Select aria-label="selectChild" className='bg-info text-white fs-10' value={selectedChild} onChange={(e) => {
              const selectedId = e.target.value;
              const child = children.find(c => c.id === parseInt(selectedId));
              setSelectedChild(selectedId);
              setSelectedChildStatus(child?.payment_status);
            }} disabled={children.length == 0 || isRenewal}>
              <option>Choose Your Child</option>
              {children.map((child, index) => (
                <option key={index} value={child.id}>Buying For {child.child_name}</option>
              ))}
            </Form.Select>
          </Col>
        </Row>
        {children.length == 0 &&
          <div className='mt-3 text-danger'>
            <small className='fs-10'>* You need to add a Child first to register to a course. Click <span className='text-underline text-primary pe-auto' onClick={() => navigate(`/childProfile`)}>here</span> to add your Child</small>
          </div>
        }
        {children.filter(child => child.payment_status !== 0).length === 0 && (
          <div className="text-danger mt-2 fs-10">
            No eligible children found. Please complete payment first.
          </div>
        )}
        {selectedChildStatus === 0 && (
          <div className="text-danger mt-2 fs-10">
            Payment is pending for this child. Please complete payment to continue.
          </div>
        )}
        <div className='border-2 my-4 border-bottom'></div>
        <Row>
          {subPrograms.length > 0 && (
            <Col xs='6' md='4'>
              <Form.Select aria-label="focus" className='bg-secondary bg-opacity-25 fs-10 mb-3' value={selectedFocus} onChange={(e) => handleFocusChange(e.target.value)} disabled={isRenewal}>
                <option>Choose Your Focus</option>
                {subPrograms.map((subProgram) =>
                  subProgram.images.map((image, index) => (
                    <option key={index} value={image.id}>{image.title}</option>
                  ))
                )}
              </Form.Select>
            </Col>
          )}
          <Col xs='6' md='4'>
            <Form.Select aria-label="skillLevel" className='bg-secondary fs-10 bg-opacity-25 mb-3' value={selectedSkillLevel} onChange={(e) => { setSelectedSkillLevel(e.target.value); fetchTimeSlots(e.target.value); }} disabled={isRenewal}>
              <option>Choose Skill Level</option>
              {filteredSkillLevels.map(skillLevel => (
                <option key={skillLevel.id} value={skillLevel.id}>{skillLevel.skill_name}</option>
              ))}
            </Form.Select>
          </Col>
        </Row>
        <Row>
          <Col xs='8' md='4'>
            <Form.Select aria-label="paymentPlan" className='bg-secondary fs-10 bg-opacity-25 mb-3' value={selectedPaymentPlan} onChange={(e) => setSelectedPaymentPlan(e.target.value)}>
              <option>Choose Payment Plan</option>
              {paymentPlans.map((plan, index) => (
                <option key={index} value={plan.id}>{plan.duration_months} Months - ₹ {plan.final_amount} {plan.discount_percent > 0 && `(-${plan.discount_percent}% Off)`}</option>
              ))}
            </Form.Select>
          </Col>
        </Row>

        <Row className='mt-4'>
          <Col md='12'><h5 className='text-danger fs-12'>Slot - 1</h5></Col>
          <Col xs='6' md='4' className='mb-3'>
            <Form.Select className='fs-10' value={selectedDay1} onChange={(e) => setSelectedDay1(e.target.value)}>
              <option value="">Select Day</option>
              {uniqueDays.map((day, index) => (
                <option key={`day1-${index}`} value={day}>{["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"][day]}</option>
              ))}
            </Form.Select>
          </Col>
          <Col xs='6' md='4' className='mb-3'>
            <Form.Select value={selectedTime1} className='fs-10' onChange={(e) => setSelectedTime1(e.target.value)}>
              <option value="">Select Time</option>
              {getTimeRangesForDay(selectedDay1).map((timeRange, index) => (
                <option key={`time1-${index}`} value={`${timeRange.start_time}-${timeRange.end_time}`}>
                  {timeRange.start_time} - {timeRange.end_time} (Available Slots: {parseInt(timeRange.available_slots) - (parseInt(timeRange.booked_slots) || 0)})
                </option>
              ))}
            </Form.Select>
          </Col>
        </Row>
        <Row>
          <Col md='12'><h5 className='text-danger fs-12'>Slot - 2</h5></Col>
          <Col xs='6' md='4' className='mb-3'>
            <Form.Select value={selectedDay2} className='fs-10' onChange={(e) => setSelectedDay2(e.target.value)}>
              <option value="">Select Day</option>
              {uniqueDays.map((day, index) => (
                <option key={`day2-${index}`} value={day}>{["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"][day]}</option>
              ))}
            </Form.Select>
          </Col>
          <Col xs='6' md='4' className='mb-3'>
            <Form.Select value={selectedTime2} className='fs-10' onChange={(e) => setSelectedTime2(e.target.value)}>
              <option value="">Select Time</option>
              {getTimeRangesForDay(selectedDay2).map((timeRange, index) => (
                <option key={`time1-${index}`} value={`${timeRange.start_time}-${timeRange.end_time}`}>
                  {timeRange.start_time} - {timeRange.end_time} (Available Slots: {timeRange.available_slots})
                </option>
              ))}
            </Form.Select>
          </Col>
        </Row>

        <Button variant='warning' type='button' className='fw-7 fs-12' onClick={handleAddToCart} disabled={selectedChildStatus === 0}>Add to cart</Button>
        <div className='mt-3 text-danger mb-5'>
          <p className='mb-0 fs-10'>Note:</p>
          <small className='fs-10'>If the slot is full, please select another slot or contact the Pied Piper office for assistance.</small>
          <p className='mt-2 text-dark fw-7 fs-10'><img src={call} width={20} className='me-2' alt="link" /> 9052000370 / 371 / 372 / 373</p>
        </div>
      </Container>
    </div>
  );
}

export default PickList;